// Advanced Graphs — full course content for the DSA Explanation Modal.
// Java-focused. Same section-course schema as graphs.js: concept → internals →
// the pattern's sub-variants → errors → complexity → beginner drills → cheatsheet → playground.

import advancedGraphDrills from './advancedGraphDrills';
const drill = id => advancedGraphDrills.find(d => d.id === id);

const advancedGraphs = {
  key: 'Advanced Graphs',
  tagline: 'Not every edge is worth the same — once connections have a COST, "shortest" and "cheapest" become genuinely different questions.',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to Weighted Graphs',
      type: 'text',
      body: [
        "Every graph technique covered so far treats all edges as equal — BFS finds the shortest path by counting HOPS, because every hop costs exactly the same 1 unit. A weighted graph breaks that assumption: each edge carries its own cost (distance, price, time, risk), and \"shortest\" now means \"cheapest total weight,\" which is not necessarily the path with the fewest edges at all.",
        "This single change — giving edges a cost — is what separates this pattern from plain Graphs. It's also why BFS alone is no longer enough, and why a whole family of new algorithms exists: Dijkstra's algorithm, Bellman-Ford, and the minimum-spanning-tree algorithms (Prim's and Kruskal's).",
      ],
    },
    {
      id: 'why',
      title: 'Why Weighted Graph Algorithms?',
      type: 'text',
      body: [
        "A road with 5 traffic lights and a highway with none aren't equally \"1 hop\" in any meaningful sense — one takes far longer. The moment a real-world problem involves distance, cost, time, or any other varying quantity per connection, plain BFS silently gives the wrong answer, because it optimizes for fewest edges, not lowest total weight.",
        "Dijkstra's algorithm generalizes BFS's \"expand the closest thing first\" idea to weighted edges, using a priority queue instead of a plain queue. Bellman-Ford handles a case Dijkstra can't — negative weights — and can additionally detect when a negative CYCLE makes \"shortest path\" undefined altogether. Minimum Spanning Tree algorithms answer a related but different question: not the cheapest path between two specific points, but the cheapest way to connect EVERY point together at once.",
      ],
    },
    {
      id: 'analogy',
      title: 'How Weighted Graphs Work — GPS Navigation and Laying Cable',
      type: 'text',
      heading: 'Expanding by True Cost, Not by Hop Count',
      body: [
        "Picture a GPS app calculating the fastest route. It doesn't just count intersections — a route through 2 highway segments can easily beat a route through 1 shortcut if that shortcut runs through slow city traffic. The GPS effectively expands outward from the start, always exploring the currently-cheapest-to-reach unexplored road next — not the nearest by hop count, but the nearest by actual travel cost. That's exactly Dijkstra's algorithm: a priority queue standing in for \"always process whatever is currently cheapest to reach.\"",
        "Now picture a telecom company that needs to lay cable connecting five towns, and wants to spend as little as possible on cable while making sure every town can reach every other (even indirectly, through other towns). It doesn't need the cheapest cable between every SPECIFIC pair — it needs the cheapest possible NETWORK that connects all five, with zero redundant connections. That's a Minimum Spanning Tree: not a shortest path between two points, but the cheapest way to connect everything at once.",
      ],
    },
    {
      id: 'visualize',
      title: 'Visualizing a Weighted Graph',
      type: 'graph-diagram',
      body: [
        "Each edge now carries a number — its weight or cost. The shortest path from node 0 to node 3 is NOT the fewest-hop path: 0→1→3 is 2 hops but costs 4+5=9, while 0→2→1→3 is 3 hops but only costs 1+2+5=8. More hops, less total cost — exactly the situation plain BFS can't handle.",
        "This is the entire reason Dijkstra's algorithm exists: it correctly finds the true cheapest path (0→2→1→3, cost 8), something hop-counting alone cannot do.",
      ],
      demo: {
        nodes: [0, 1, 2, 3],
        edges: [[0, 1, 4], [0, 2, 1], [2, 1, 2], [1, 3, 5], [2, 3, 8]],
        directed: false,
        note: 'The cheapest path from 0 to 3 is 0→2→1→3 (cost 1+2+5=8) — one more hop than 0→1→3 (cost 4+5=9), but strictly cheaper overall.',
      },
    },
    {
      id: 'representation',
      title: 'Representing a Weighted Graph in Java',
      type: 'code',
      code:
`// Each adjacency-list entry now carries a weight alongside the neighbor
Map<Integer, List<int[]>> adj = new HashMap<>();
for (int[] edge : edges) {   // edge = { from, to, weight }
    adj.computeIfAbsent(edge[0], k -> new ArrayList<>()).add(new int[] { edge[1], edge[2] });
    adj.computeIfAbsent(edge[1], k -> new ArrayList<>()).add(new int[] { edge[0], edge[2] });   // omit if directed
}`,
      note: 'The only structural change from an unweighted adjacency list: each entry is now a {neighbor, weight} pair instead of a bare neighbor value.',
    },
    {
      id: 'dijkstra',
      title: "Dijkstra's Algorithm",
      type: 'code',
      code:
`int[] dijkstra(int src, int n, Map<Integer, List<int[]>> adj) {
    int[] dist = new int[n];
    Arrays.fill(dist, Integer.MAX_VALUE);
    dist[src] = 0;
    PriorityQueue<int[]> pq = new PriorityQueue<>((a, b) -> a[1] - b[1]);   // {node, distance}
    pq.add(new int[] { src, 0 });

    while (!pq.isEmpty()) {
        int[] top = pq.poll();
        int node = top[0], d = top[1];
        if (d > dist[node]) continue;   // a better distance was already found — stale entry

        for (int[] edge : adj.get(node)) {
            int neighbor = edge[0], weight = edge[1];
            if (dist[node] + weight < dist[neighbor]) {
                dist[neighbor] = dist[node] + weight;
                pq.add(new int[] { neighbor, dist[neighbor] });
            }
        }
    }
    return dist;
}`,
      note: 'This is BFS with a priority queue standing in for a plain queue — "always expand the closest thing next" now means closest by TOTAL WEIGHT, not by hop count. Dijkstra requires all weights to be non-negative to work correctly.',
    },
    {
      id: 'bellmanford',
      title: 'Bellman-Ford (Negative Weights, and Negative Cycle Detection)',
      type: 'code',
      code:
`int[] bellmanFord(int src, int n, int[][] edges) {
    int[] dist = new int[n];
    Arrays.fill(dist, Integer.MAX_VALUE);
    dist[src] = 0;

    for (int round = 0; round < n - 1; round++) {          // V-1 rounds always suffice, if no negative cycle
        for (int[] edge : edges) {
            if (dist[edge[0]] != Integer.MAX_VALUE && dist[edge[0]] + edge[2] < dist[edge[1]]) {
                dist[edge[1]] = dist[edge[0]] + edge[2];
            }
        }
    }

    for (int[] edge : edges) {                              // one MORE round — a check, not a real relaxation
        if (dist[edge[0]] != Integer.MAX_VALUE && dist[edge[0]] + edge[2] < dist[edge[1]]) {
            throw new IllegalStateException("Negative cycle detected");
        }
    }
    return dist;
}`,
      note: 'Unlike Dijkstra, Bellman-Ford handles negative edge weights correctly — and its extra "check round" is a built-in way to detect a negative CYCLE, which makes "shortest path" undefined (you could loop the cycle forever, decreasing cost each time).',
    },
    {
      id: 'mst',
      title: 'Minimum Spanning Tree: Prim\'s and Kruskal\'s',
      type: 'code',
      code:
`// Prim's — grows the MST outward from one starting node
PriorityQueue<int[]> pq = new PriorityQueue<>((a, b) -> a[1] - b[1]);   // {node, weight}
// ... push edges from the frontier, always take the cheapest, skip stale (already-in) entries ...

// Kruskal's — sorts ALL edges once, adds greedily via Union-Find
Arrays.sort(edges, (a, b) -> a[2] - b[2]);
for (int[] edge : edges) {
    if (find(edge[0]) != find(edge[1])) {   // would this edge connect two different components?
        union(edge[0], edge[1]);
        // add edge[2] to the running total
    }
}`,
      note: 'Both find the exact same minimum total weight — Prim\'s grows outward like Dijkstra (good for dense graphs), Kruskal\'s processes a sorted edge list with Union-Find (good for sparse graphs, or when edges are already given as a flat list).',
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'Using Dijkstra\'s algorithm on a graph with negative edge weights',
          why: 'Dijkstra assumes that once a node is finalized (popped from the priority queue), its distance can never improve — a negative edge can violate that assumption, silently producing a wrong answer with no error or warning.',
          fix: 'Use Bellman-Ford whenever negative weights are possible — Dijkstra is only correct for non-negative weights.',
        },
        {
          mistake: 'Relaxing edges against a live, already-updated array in bounded-round Bellman-Ford variants',
          why: 'When a round count is meant to correspond to a maximum number of EDGES used (like in Cheapest Flights Within K Stops), relaxing against values already updated earlier in the SAME round can silently let a path use more edges than the round count allows.',
          fix: 'Take a snapshot of distances at the start of each round, and relax using only that frozen snapshot — never a value updated within the current round.',
        },
        {
          mistake: 'Forgetting the stale-entry check when popping from Dijkstra\'s priority queue',
          why: 'A node can be pushed onto the queue multiple times as its distance keeps improving — without checking whether the popped distance still matches the current best-known one, a stale (outdated) entry can cause incorrect relaxations.',
          fix: 'Always check if (poppedDistance > dist[node]) continue; immediately after popping, before doing any relaxation work.',
        },
        {
          mistake: 'Confusing "shortest path between two nodes" with "minimum spanning tree"',
          why: 'These solve genuinely different problems — Dijkstra/Bellman-Ford answer "what\'s the cheapest way from A to B specifically," while Prim\'s/Kruskal\'s answer "what\'s the cheapest way to connect EVERYTHING," and an MST\'s tree edges do NOT generally form the shortest path between any two given nodes.',
          fix: 'Identify which question is actually being asked before picking an algorithm — a single source/destination points to Dijkstra or Bellman-Ford; "connect everything as cheaply as possible" points to an MST algorithm.',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: "Dijkstra's, with a min-heap", time: 'O((V + E) log V)', note: 'Requires non-negative edge weights.' },
        { op: 'Bellman-Ford', time: 'O(V · E)', note: 'Slower than Dijkstra, but handles negative weights and detects negative cycles.' },
        { op: "Prim's MST, with a min-heap", time: 'O(E log E)', note: 'Grows outward from one starting node.' },
        { op: "Kruskal's MST, with Union-Find", time: 'O(E log E)', note: 'Dominated by sorting all edges once.' },
        { op: 'Floyd-Warshall (all-pairs shortest path)', time: 'O(V³)', note: 'Simple DP; handles negative edges (not negative cycles); best for smaller, dense graphs.' },
      ],
    },
    {
      id: 'when',
      title: 'When to Use',
      type: 'text',
      heading: 'Reach for weighted graph algorithms when you see…',
      body: [
        'Edges with an explicit cost, distance, time, or weight — not just a plain connection.',
        '"Cheapest/shortest path from A to B" with non-negative weights — Dijkstra.',
        '"Cheapest/shortest path" where weights CAN be negative, or a negative cycle needs to be detected — Bellman-Ford.',
        '"Cheapest way to connect every node" (not a specific pair) — Prim\'s or Kruskal\'s, a Minimum Spanning Tree.',
        'Every pair of nodes needs a shortest path at once, on a small-to-moderate graph — Floyd-Warshall.',
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('weighted-adjacency-list'),
            drill('shortest-path-unweighted-bfs'),
            drill('network-delay-time-drill'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('dijkstra-from-scratch'),
            drill('minimum-spanning-tree-prims'),
            drill('minimum-spanning-tree-kruskals'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('cheapest-flights-k-stops'),
            drill('floyd-warshall-all-pairs'),
            drill('detect-negative-cycle-bellman-ford'),
          ],
        },
      ],
      note: 'These are foundation drills to build comfort with Dijkstra\'s, Bellman-Ford, and MST algorithms before moving into the curated interview problem for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'Dijkstra\'s = BFS with a priority queue instead of a plain queue — only valid for non-negative weights.',
        'Bellman-Ford handles negative weights, and its extra "check round" detects negative cycles — O(V·E), slower than Dijkstra but more general.',
        'A bounded-round Bellman-Ford variant (like Cheapest Flights Within K Stops) MUST relax against a frozen snapshot, never live in-round values.',
        'Prim\'s and Kruskal\'s both find a Minimum Spanning Tree — the cheapest way to connect every node, not the cheapest path between two specific ones.',
        'Prim\'s grows outward with a heap; Kruskal\'s sorts all edges once and uses Union-Find — both O(E log E).',
        'Floyd-Warshall computes all-pairs shortest paths in O(V³) via a simple triple-nested loop — the outer loop MUST be the waypoint node k.',
        'Always check for a stale priority-queue entry after popping in Dijkstra\'s — a node can be pushed multiple times as its distance improves.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default advancedGraphs;
